import React, { useEffect, useRef, useState } from 'react';
import { Document, PDFViewer, Font, Text, View, Image, Link } from '@react-pdf/renderer';
import fontRegular from 'src/assets/fonts/Poppins-Regular.ttf';
import fontMedium from 'src/assets/fonts/Poppins-Medium.ttf';
import fontBold from 'src/assets/fonts/Poppins-Bold.ttf';
import fontItalic from 'src/assets/fonts/Poppins-Italic.ttf';
import { AssessmentHead, HtmlWrap } from '../comps';
import { IS_DEBUG } from '../conf';
import { DashboardQuery } from 'src/graphql/_generated';
import { AssessmentEnd } from '../comps';
import { colorsApp } from 'src/redux/reducers/theme';
import CardsPage from './CardsPage';
import { AssessmentState } from 'src/pages/InsightsPage/V2/types';
import { determineState, sortRiskyScenariosByLiveExpectedLoss } from 'src/pages/InsightsPage/V2/util';
import { cardStyles } from './styles';
import { CashPlaceholder, Danger } from './CardsPage/comps';
import { numeralFormat, formatNumber } from 'src/utils/misc';
import _ from 'lodash';
import { customNumberFormatRI } from 'src/pages/InsightsPage/V2/widgets';
import { getExternalPath } from '../util';
import {
  extLinkImg,
  tilelineActionIcon,
  tilelineActionIconGray,
  timelineChartIcon,
  timelineCompileIcon,
  timelineListIcon,
  timelineMagCheckIcon,
  timelineShieldIcon,
  timelineWarnIcon,
} from '../imgData';
import { useOrganisationBranding } from 'src/hooks/useOrganisationBranding';
import { useAuth } from 'src/state/auth';
import { TimelineEvent } from 'src/api/types/dashboard';
import moment from 'moment';
import { getUniqueLinkedControlsCount } from 'src/pages/InsightsPage/util';

Font.registerHyphenationCallback((word) => [word]);

Font.register({
  family: 'Poppins',

  fonts: [
    {
      src: fontRegular,
      fontWeight: 400,
    },
    {
      src: fontMedium,
      fontWeight: 500,
    },
    {
      src: fontBold,
      fontWeight: 600,
    },
    {
      src: fontItalic,
      fontWeight: 400,
      fontStyle: 'italic',
    },
  ],
});

const colors = colorsApp['light'];

interface PdfInsightsProps {
  onClose: () => void;
  data: NonNullable<DashboardQuery>;
  chartImage?: string | null;
  events: TimelineEvent[];
}

const PdfInsights: React.FC<PdfInsightsProps> = ({ onClose, data, chartImage, events }) => {
  const [state, setAssessmentState] = useState<AssessmentState>('ALL_INFO_AVAILABLE');
  const { profile } = useAuth();
  const {
    logoUrl: brandingLogo,
    primaryColor: brandingPrimaryColor,
    secondaryColor: brandingSecondaryColor,
  } = useOrganisationBranding();
  const hasDownloaded = useRef(false);

  useEffect(() => {
    if (data?.assessment) {
      setAssessmentState(determineState(data.assessment));
    }
  }, [data]);

  const print = (blob: Blob | undefined) => {
    if (blob) {
      let elm = document.createElement('a');
      elm.href = URL.createObjectURL(blob);
      elm.setAttribute('download', `${data.assessment?.name}-insights.pdf`);
      elm.click();
      elm.remove();

      onClose();
    }
  };

  const current = customNumberFormatRI(data.dashboard.returnOnInvestment?.currentProgramme);
  const future = customNumberFormatRI(data.dashboard.returnOnInvestment?.futureProgramme);
  const riskyScenarios = sortRiskyScenariosByLiveExpectedLoss(data.dashboard.riskyScenarios?.scenarios);

  const hasIncidents = data?.assessment?.scenarios?.some((scenario) => !!scenario.incidents.length);

  if (!data) return null;

  return (
    <HtmlWrap $isTest={IS_DEBUG}>
      <PDFViewer>
        <Document
          onRender={(el) => {
            if (!IS_DEBUG && !hasDownloaded.current) {
              hasDownloaded.current = true;
              print(el.blob);
            }
          }}
        >
          <AssessmentHead
            id="toc"
            label="INSIGHTS"
            colors={colors}
            orientation="landscape"
            assessment={{
              name: data.assessment?.name!,
              description: data.assessment?.description!,
              startedDate: data.assessment?.startedDate!,
              updatedAt: data.assessment?.meta?.updatedAt!,
              editedAt: data.assessment?.editedAt!,
            }}
            exportedBy={profile?.user?.name!}
            activeWorkspaceName={profile?.activeWorkspace?.name!}
            logoSrc={brandingLogo}
            primaryColor={brandingPrimaryColor}
            secondaryColor={brandingSecondaryColor}
          />

          <CardsPage
            colors={colors}
            logoSrc={brandingLogo}
            slot1={{
              title: 'Overview',
              children: (
                <>
                  <View style={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                    <View style={cardStyles.column}>
                      <Text style={cardStyles.label}>SCENARIOS</Text>
                      <Text style={cardStyles.subLabelItalic}>in the assessment</Text>
                      <View style={{ flex: 1 }} />
                      {state === 'NO_SCENARIOS' ? (
                        <Danger />
                      ) : (
                        <Text style={cardStyles.value}>{data.assessment?.scenarios?.length}</Text>
                      )}
                    </View>
                    <View style={cardStyles.column}>
                      <Text style={cardStyles.label}>CONTROLS</Text>
                      <Text style={cardStyles.subLabelItalic}>linked to scenarios</Text>
                      <View style={{ flex: 1 }} />
                      {state === 'NO_SCENARIOS' || state === 'SCENARIOS_NO_CONTROLS' ? (
                        <Danger />
                      ) : (
                        <Text style={cardStyles.value}>{getUniqueLinkedControlsCount(data.assessment)}</Text>
                      )}
                    </View>
                    <View style={cardStyles.column}>
                      <Text style={cardStyles.label}>MODIFIERS</Text>
                      <Text style={cardStyles.subLabelItalic}>refelecting how the controls reduce risk</Text>
                      <View style={{ flex: 1 }} />
                      {state === 'NO_SCENARIOS' ||
                      state === 'SCENARIOS_NO_CONTROLS' ||
                      state === 'SCENARIOS_CONTROLS_NO_MODIFIERS' ? (
                        <Danger />
                      ) : (
                        <Text style={cardStyles.value}>
                          {data.assessment?.scenarios?.reduce(
                            (acc, scenario) =>
                              acc +
                              scenario.riskManagementPlans.reduce(
                                (planAcc, plan) => planAcc + plan.modifiers.length,
                                0,
                              ),
                            0,
                          )}
                        </Text>
                      )}
                    </View>
                  </View>
                </>
              ),
            }}
            slot2={{
              title: 'Expected Loss',
              children: (
                <>
                  <Text style={{ ...cardStyles.subLabel, marginBottom: 20 }}>
                    The assessment expected loss represents the probability adjusted, mean loss if each risk were to
                    manifest in the same year.
                  </Text>
                  <View style={{ flex: 1 }} />

                  {state === 'NO_SCENARIOS' ||
                  state === 'ALL_INFO_AVAILABLE' ||
                  state === 'SCENARIOS_LIVE_CONTROLS_MODIFIERS' ||
                  state === 'SCENARIOS_PENDING_CONTROLS_MODIFIERS' ? (
                    <View style={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                      <View style={{ ...cardStyles.column, height: 32 }}>
                        <Text style={cardStyles.labelSmall}>UNMODIFIED</Text>
                        <View style={{ flex: 1 }} />
                        {state === 'NO_SCENARIOS' ? (
                          <CashPlaceholder />
                        ) : (
                          <Text style={cardStyles.value}>
                            {numeralFormat(data.dashboard.expectedLoss?.unmodifiedRisk)}
                          </Text>
                        )}
                      </View>
                      <View style={{ ...cardStyles.column, height: 32 }}>
                        <Text style={cardStyles.labelSmall}>LIVE</Text>
                        <View style={{ flex: 1 }} />
                        {state === 'NO_SCENARIOS' ? (
                          <CashPlaceholder />
                        ) : (
                          <Text style={{ ...cardStyles.value, color: colors.gp1 }}>
                            {numeralFormat(data.dashboard.expectedLoss?.liveRisk)}
                          </Text>
                        )}
                      </View>
                      <View style={{ ...cardStyles.column, height: 32 }}>
                        <Text style={cardStyles.labelSmall}>PROJECTED</Text>
                        <View style={{ flex: 1 }} />
                        {state === 'NO_SCENARIOS' ? (
                          <CashPlaceholder />
                        ) : (
                          <Text style={{ ...cardStyles.value, color: colors.brightBlue }}>
                            {numeralFormat(data.dashboard.expectedLoss?.projectedRisk)}
                          </Text>
                        )}
                      </View>
                    </View>
                  ) : (
                    <Text style={{ ...cardStyles.value, color: colors.gp1 }}>
                      {numeralFormat(data.dashboard.expectedLoss?.unmodifiedRisk)}
                    </Text>
                  )}
                </>
              ),
            }}
            slot3={{
              title: 'Risky Scenarios',
              children: (
                <>
                  <Text style={{ ...cardStyles.subLabel }}>
                    These scenarios have the largest contribution to your overall risk posture.
                  </Text>
                  <View style={{ flex: 1 }} />
                  {state === 'NO_SCENARIOS' ? (
                    <Danger />
                  ) : (
                    <View style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {riskyScenarios.map((scenario, idx) => (
                        <View key={scenario.id} style={{ ...cardStyles.legendRow, marginBottom: 1 }}>
                          <Link
                            src={getExternalPath(`risk-assessments/${scenario.assessment.id}/scenario/${scenario.id}`)}
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              textDecoration: 'none',
                              gap: 4,
                            }}
                          >
                            <Text
                              style={{
                                ...cardStyles.legendLabel,
                                color: colors.prussianBlue,
                                textDecoration: 'underline',
                              }}
                            >
                              {idx + 1}. {scenario.name}
                            </Text>
                            {/* <Image src={extLinkImg} style={{ width: 6, height: 6 }} /> */}
                          </Link>
                          <View style={cardStyles.separator} />
                          <Text style={{ ...cardStyles.legendValue, color: colors.gp1 }}>
                            {numeralFormat(scenario.liveRisk.expectedLoss)}
                          </Text>
                        </View>
                      ))}
                    </View>
                  )}
                </>
              ),
            }}
            slot4={{
              title: 'Current Security Programme',
              children: (
                <View>
                  <View style={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
                    <View style={cardStyles.column}>
                      <Text style={cardStyles.label}>RISK REDUCTION</Text>
                      <Text style={cardStyles.subLabelItalic}>between unmodified and live risk</Text>
                      <View style={{ flex: 1 }} />
                      {state === 'NO_SCENARIOS' ||
                      state === 'SCENARIOS_NO_CONTROLS' ||
                      state === 'SCENARIOS_CONTROLS_NO_MODIFIERS' ? (
                        <CashPlaceholder />
                      ) : (
                        <Text style={{ ...cardStyles.value, color: colors.gp1 }}>
                          {numeralFormat(data.dashboard.securityProgram?.riskReduction)}
                        </Text>
                      )}
                    </View>
                    <View style={cardStyles.column}>
                      <Text style={cardStyles.label}>COST</Text>
                      <Text style={cardStyles.subLabelItalic}>
                        of the existing control required to achieve this reduction
                      </Text>
                      <View style={{ flex: 1 }} />
                      {state === 'NO_SCENARIOS' ||
                      state === 'SCENARIOS_NO_CONTROLS' ||
                      state === 'SCENARIOS_CONTROLS_NO_MODIFIERS' ? (
                        <CashPlaceholder />
                      ) : (
                        <Text style={{ ...cardStyles.value, color: colors.gp1 }}>
                          {numeralFormat(data.dashboard.securityProgram?.cost)}
                        </Text>
                      )}
                    </View>
                    <View style={cardStyles.column}>
                      <Text style={cardStyles.label}>SAVINGS</Text>
                      <Text style={cardStyles.subLabelItalic}>as a result of the security program</Text>
                      <View style={{ flex: 1 }} />
                      {state === 'NO_SCENARIOS' ||
                      state === 'SCENARIOS_NO_CONTROLS' ||
                      state === 'SCENARIOS_CONTROLS_NO_MODIFIERS' ? (
                        <CashPlaceholder />
                      ) : (
                        <Text style={{ ...cardStyles.value, color: colors.gp1 }}>
                          {numeralFormat(data.dashboard.securityProgram?.savings)}
                        </Text>
                      )}
                    </View>
                  </View>
                </View>
              ),
            }}
            slot5={{
              title: 'Future Security Programme',
              children: (
                <View>
                  <View style={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
                    <View style={cardStyles.column}>
                      <Text style={cardStyles.label}>FURTHER REDUCTION</Text>
                      <Text style={cardStyles.subLabelItalic}>between live and projected risk</Text>
                      <View style={{ flex: 1 }} />
                      {state === 'NO_SCENARIOS' ||
                      state === 'SCENARIOS_NO_CONTROLS' ||
                      state === 'SCENARIOS_CONTROLS_NO_MODIFIERS' ? (
                        <CashPlaceholder />
                      ) : (
                        <Text style={{ ...cardStyles.value, color: colors.brightBlue }}>
                          {numeralFormat(data.dashboard.futureSecurityProgram?.furtherReduction)}
                        </Text>
                      )}
                    </View>
                    <View style={cardStyles.column}>
                      <Text style={cardStyles.label}>ADDITIONAL COST</Text>
                      <Text style={cardStyles.subLabelItalic}>
                        of the additional controls required to achieve this reduction
                      </Text>
                      <View style={{ flex: 1 }} />
                      {state === 'NO_SCENARIOS' ||
                      state === 'SCENARIOS_NO_CONTROLS' ||
                      state === 'SCENARIOS_CONTROLS_NO_MODIFIERS' ? (
                        <CashPlaceholder />
                      ) : (
                        <Text style={{ ...cardStyles.value, color: colors.brightBlue }}>
                          {numeralFormat(data.dashboard.futureSecurityProgram?.additionalCost)}
                        </Text>
                      )}
                    </View>
                    <View style={cardStyles.column}>
                      <Text style={cardStyles.label}>TOTAL SAVINGS</Text>
                      <Text style={cardStyles.subLabelItalic}>including the future security program</Text>
                      <View style={{ flex: 1 }} />
                      {state === 'NO_SCENARIOS' ||
                      state === 'SCENARIOS_NO_CONTROLS' ||
                      state === 'SCENARIOS_CONTROLS_NO_MODIFIERS' ? (
                        <CashPlaceholder />
                      ) : (
                        <Text style={{ ...cardStyles.value, color: colors.brightBlue }}>
                          {numeralFormat(data.dashboard.futureSecurityProgram?.totalSavings)}
                        </Text>
                      )}
                    </View>
                  </View>
                </View>
              ),
            }}
            slot6={{
              title: 'Return on Investment',
              children: (
                <>
                  <Text style={{ ...cardStyles.subLabel, marginBottom: 22 }}>
                    The risk reduction divided by the cost of your security programme.
                  </Text>
                  <View style={{ flex: 1 }} />
                  <View style={{ display: 'flex', flexDirection: 'row', gap: 1, paddingBottom: 1 }}>
                    <View style={{ ...cardStyles.column, height: 32, width: '40%' }}>
                      <Text style={cardStyles.labelSmall}>CURRENT PROGRAMME</Text>
                      <View style={{ flex: 1 }} />
                      {state === 'SCENARIOS_LIVE_CONTROLS_MODIFIERS' ||
                      state === 'ALL_INFO_AVAILABLE' ||
                      state === 'SCENARIOS_PENDING_CONTROLS_MODIFIERS' ? (
                        <>
                          <Text style={{ ...cardStyles.value, color: colors.gp1 }}>
                            {current}
                            {current ? 'x' : ''}
                          </Text>
                        </>
                      ) : (
                        <Danger />
                      )}
                    </View>
                    <View style={{ ...cardStyles.column, height: 32 }}>
                      <Text style={cardStyles.labelSmall}>FUTURE PROGRAMME</Text>
                      <View style={{ flex: 1 }} />
                      {state === 'SCENARIOS_LIVE_CONTROLS_MODIFIERS' ||
                      state === 'ALL_INFO_AVAILABLE' ||
                      state === 'SCENARIOS_PENDING_CONTROLS_MODIFIERS' ? (
                        <>
                          <Text style={{ ...cardStyles.value, color: colors.brightBlue }}>
                            {future}
                            {future ? 'x' : ''}
                          </Text>
                        </>
                      ) : (
                        <Danger />
                      )}
                    </View>
                  </View>
                </>
              ),
            }}
          />
          <CardsPage
            colors={colors}
            logoSrc={brandingLogo}
            slot1={{
              title: 'Control Implementation',
              children: (
                <>
                  {state === 'NO_SCENARIOS' || state === 'SCENARIOS_NO_CONTROLS' ? (
                    <>
                      <View style={{ flex: 1 }} />
                      <Danger />
                    </>
                  ) : (
                    <View style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
                      <View style={cardStyles.chartContainer}>
                        {chartImage && (
                          <Image
                            src={chartImage}
                            style={{
                              width: 86,
                              height: 86,
                            }}
                          />
                        )}
                      </View>
                      <View style={cardStyles.legendSection}>
                        <View style={cardStyles.legendRow}>
                          <Text style={cardStyles.legendLabel}>
                            Not Started ({data.dashboard.controlImplementation?.notStartedCount || 0})
                          </Text>
                          <View style={cardStyles.separator} />
                          <Text style={{ ...cardStyles.legendValue, color: colors.prussianBlue }}>
                            {numeralFormat(data.dashboard.controlImplementation?.notStartedCost)}
                          </Text>
                        </View>
                        <View style={cardStyles.legendRow}>
                          <Text style={cardStyles.legendLabel}>
                            In Progress ({data.dashboard.controlImplementation?.inProgressCount || 0})
                          </Text>
                          <View style={cardStyles.separator} />
                          <Text style={{ ...cardStyles.legendValue, color: colors.brightBlue }}>
                            {numeralFormat(data.dashboard.controlImplementation?.inProgressCost)}
                          </Text>
                        </View>
                        <View style={cardStyles.legendRow}>
                          <Text style={cardStyles.legendLabel}>
                            Live ({data.dashboard.controlImplementation?.liveCount || 0})
                          </Text>
                          <View style={cardStyles.separator} />
                          <Text style={{ ...cardStyles.legendValue, color: colors.gp1 }}>
                            {numeralFormat(data.dashboard.controlImplementation?.liveCost)}
                          </Text>
                        </View>
                        <View style={cardStyles.legendRowSpaced}>
                          <Text style={cardStyles.legendLabel}>
                            Other ({data.dashboard.controlImplementation?.otherCount || 0})
                          </Text>
                          <View style={cardStyles.separator} />
                          <Text style={{ ...cardStyles.legendValue, color: colors.cflowerBlue }}>
                            {numeralFormat(data.dashboard.controlImplementation?.otherCost)}
                          </Text>
                        </View>
                        <View style={cardStyles.legendRowLast}>
                          <Text style={cardStyles.legendLabel}>Controls without Cost</Text>
                          <Text style={{ ...cardStyles.legendValue, color: colors.prussianBlue }}>
                            {data.dashboard.controlImplementation?.controlsWithoutCost || 0}
                          </Text>
                        </View>
                      </View>
                    </View>
                  )}
                </>
              ),
            }}
            slot2={{
              title: 'Overdue Reviews',
              children: (
                <>
                  <Text style={{ ...cardStyles.subLabel, marginBottom: 22 }}>
                    You should review your risk scenarios on a regular basis.
                  </Text>
                  <View style={{ flex: 1 }} />
                  {state === 'NO_SCENARIOS' ? (
                    <Danger />
                  ) : (
                    <View style={cardStyles.overdueChip}>
                      <Text style={cardStyles.overdueValue}>{data.dashboard.overdueReviews?.scenarios || 0} </Text>
                      <Text style={cardStyles.overdueLabel}>overdue</Text>
                    </View>
                  )}
                </>
              ),
            }}
            slot3={{
              title: 'Upcoming Actions',
              children: (
                <>
                  {events.length === 0 ? (
                    <View style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                      <Text style={{ ...cardStyles.legendLabel, color: colors.cflowerBlue }}>No upcoming actions</Text>
                    </View>
                  ) : (
                    <View style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                      {events.map((event, idx) => {
                        const eventDate = event.event_date;
                        const formattedDate = moment(eventDate).format('DD-MM-YYYY');
                        const isToday = moment(eventDate).startOf('day').diff(moment().startOf('day'), 'days') === 0;

                        // Determine if the event is overdue
                        const isOverdue =
                          event.type === 'ACTIONS_DUE' || moment(eventDate).isBefore(moment().startOf('day'), 'day');

                        // Determine the event text, link, and icon based on event type
                        let prefixText = '';
                        let itemName = '';
                        let suffixText = '';
                        let linkPath = '';
                        let iconSrc = '';

                        switch (event.type) {
                          case 'FUTURE_ASSESSMENT_REVIEW_DUE':
                            prefixText = 'Assessment ';
                            itemName = event.data.assessment?.name || 'Unknown';
                            suffixText = ' due for review';
                            linkPath = `risk-assessments/${event.data.assessment?.id}`;
                            iconSrc = timelineChartIcon;
                            break;
                          case 'FUTURE_SCENARIO_REVIEW_DUE':
                            prefixText = 'Risk Scenario ';
                            itemName = event.data.scenario?.name || 'Unknown';
                            suffixText = ' due for review';
                            linkPath = `risk-assessments/${event.data.assessment?.id}/scenario/${event.data.scenario?.id}`;
                            iconSrc = timelineListIcon;
                            break;
                          case 'FUTURE_CONTROL_REVIEW_DUE':
                            prefixText = 'Control ';
                            itemName = event.data.control?.name || 'Unknown';
                            suffixText = ' due for review';
                            linkPath = `controls/${event.data.control?.id}`;
                            iconSrc = timelineCompileIcon;
                            break;
                          case 'FUTURE_CONTROL_DUE_LIVE_ACTUAL':
                            prefixText = 'Control ';
                            itemName = event.data.control?.name || 'Unknown';
                            suffixText = ' due to go live';
                            linkPath = `controls/${event.data.control?.id}`;
                            iconSrc = timelineCompileIcon;
                            break;
                          case 'FUTURE_COMPLIANCE_AUDIT_DUE':
                            prefixText = '';
                            itemName = 'Compliance audit';
                            suffixText = ' due';
                            linkPath = 'compliance';
                            iconSrc = timelineMagCheckIcon;
                            break;
                          case 'FUTURE_IMPROVEMENT_PLAN_REVIEW_DUE':
                            prefixText = 'Improvement Plan ';
                            itemName = event.data.plan?.name || 'Unknown';
                            suffixText = ' due for review';
                            linkPath = `improvement-plans/${event.data.plan?.id}`;
                            iconSrc = timelineShieldIcon;
                            break;
                          case 'FUTURE_ASSESSMENT_START_DUE':
                            prefixText = 'Assessment ';
                            itemName = event.data.assessment?.name || 'Unknown';
                            suffixText = ' due to start';
                            linkPath = `risk-assessments/${event.data.assessment?.id}`;
                            iconSrc = timelineChartIcon;
                            break;
                          case 'FUTURE_SCENARIO_IDENTIFIED':
                            prefixText = 'Risk Scenario ';
                            itemName = event.data.scenario?.name || 'Unknown';
                            suffixText = ' identified';
                            linkPath = `risk-assessments/${event.data.assessment?.id}/scenario/${event.data.scenario?.id}`;
                            iconSrc = timelineListIcon;
                            break;
                          case 'FUTURE_CONTROL_DUE_LIVE_EXPECTED':
                            prefixText = 'Control ';
                            itemName = event.data.control?.name || 'Unknown';
                            suffixText = ' expected to go live';
                            linkPath = `controls/${event.data.control?.id}`;
                            iconSrc = timelineCompileIcon;
                            break;
                          case 'FUTURE_INCIDENT_CREATED':
                            prefixText = 'Incident ';
                            itemName = event.data.incident?.name || 'Unknown';
                            suffixText = ' will occur';
                            linkPath = `incidents/${event.data.incident?.id}`;
                            iconSrc = timelineWarnIcon;
                            break;
                          case 'OPEN_ACTIONS':
                            prefixText = 'Action ';
                            itemName = event.data.action?.notes || 'Unknown';
                            suffixText = isToday ? ' is due' : ' due';
                            linkPath = (() => {
                              const objectType = event.data.object?.type;
                              const objectId = event.data.object?.id;
                              const assessmentId = event.data.object?.assessmentId;
                              switch (objectType) {
                                case 'ASSESSMENT':
                                  return `risk-assessments/${objectId}#actions`;
                                case 'SCENARIO':
                                  return `risk-assessments/${assessmentId}/scenario/${objectId}#actions`;
                                case 'CONTROL':
                                  return `risk-management/control/${objectId}#actions`;
                                case 'INCIDENT':
                                  return `incidents/${objectId}#actions`;
                                default:
                                  return 'timeline';
                              }
                            })();
                            iconSrc = tilelineActionIcon;
                            break;
                          case 'ACTIONS_DUE':
                            prefixText = 'Action ';
                            itemName = event.data.action?.notes || 'Unknown';
                            suffixText = ' was due';
                            linkPath = (() => {
                              const objectType = event.data.object?.type;
                              const objectId = event.data.object?.id;
                              const assessmentId = event.data.object?.assessmentId;
                              switch (objectType) {
                                case 'ASSESSMENT':
                                  return `risk-assessments/${objectId}#actions`;
                                case 'SCENARIO':
                                  return `risk-assessments/${assessmentId}/scenario/${objectId}#actions`;
                                case 'CONTROL':
                                  return `risk-management/control/${objectId}#actions`;
                                case 'INCIDENT':
                                  return `incidents/${objectId}#actions`;
                                default:
                                  return 'timeline';
                              }
                            })();
                            iconSrc = tilelineActionIconGray;
                            break;
                          default:
                            prefixText = '';
                            itemName = 'Action item';
                            suffixText = '';
                            linkPath = 'timeline';
                            iconSrc = timelineChartIcon;
                        }

                        const fullText = `${prefixText}${itemName}${suffixText}`;
                        const truncatedText = fullText.length > 42 ? `${fullText.substring(0, 37)}...` : fullText;

                        return (
                          <View
                            key={idx}
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: 6,
                              marginBottom: 3,
                            }}
                          >
                            {/* Event icon */}
                            <Image
                              src={iconSrc}
                              style={{
                                width: 13,
                                height: 13,
                                flexShrink: 0,
                              }}
                            />

                            {/* Date */}
                            <Text
                              style={{
                                ...cardStyles.legendLabel,
                                color: isOverdue ? colors.statusRed : colors.cflowerBlue,
                                width: 44,
                                fontSize: 7,
                                flexShrink: 0,
                              }}
                            >
                              {formattedDate}
                            </Text>

                            {/* Event text as single link */}
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                              <Link
                                src={getExternalPath(linkPath)}
                                style={{
                                  textDecoration: 'none',
                                }}
                              >
                                <Text
                                  style={{
                                    ...cardStyles.legendLabel,
                                    color: colors.prussianBlue,
                                    textDecoration: 'underline',
                                    fontSize: 7,
                                  }}
                                >
                                  {truncatedText}
                                </Text>
                              </Link>
                              <View style={{ width: 4 }} />
                              {/* <Image src={extLinkImg} style={{ width: 4, height: 4 }} /> */}
                            </View>
                          </View>
                        );
                      })}

                      <View style={{ flex: 1 }} />
                      {/* View all button */}
                      <View style={{ marginTop: 8, alignSelf: 'flex-start' }}>
                        <Link
                          src={getExternalPath('timeline')}
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            textDecoration: 'none',
                            gap: 4,
                          }}
                        >
                          <Text
                            style={{
                              fontSize: 9,
                              fontFamily: 'Poppins',
                              color: colors.gp1,
                            }}
                          >
                            View all
                          </Text>
                          {/* <Image src={extLinkImg} style={{ width: 4, height: 4 }} /> */}
                        </Link>
                      </View>
                    </View>
                  )}
                </>
              ),
            }}
            slot4={{
              title: 'Average Incident Frequency',
              children: (
                <>
                  <View style={{ flex: 1 }} />
                  {state === 'NO_SCENARIOS' ? (
                    <Danger />
                  ) : (
                    <>
                      {!hasIncidents ? (
                        <Danger />
                      ) : (
                        <View>
                          <View style={cardStyles.legendRow}>
                            <Text style={cardStyles.legendLabel}>Mean time between incidents</Text>
                            <View style={cardStyles.separator} />
                            {_.isNil(data.dashboard.averageIncidentFrequency?.meantimeBetweenIncidents) ? (
                              <Text style={{ ...cardStyles.legendValue, color: colors.gp1 }}>N/A</Text>
                            ) : (
                              <Text style={{ ...cardStyles.legendValue, color: colors.gp1 }}>
                                {formatNumber(data.dashboard.averageIncidentFrequency?.meantimeBetweenIncidents)} days
                              </Text>
                            )}
                          </View>
                          <View style={cardStyles.legendRowLast}>
                            <Text style={cardStyles.legendLabel}>Mean time between near misses</Text>
                            <View style={cardStyles.separator} />
                            {_.isNil(data.dashboard.averageIncidentFrequency?.meantimeBetweenNearMisses) ? (
                              <Text style={{ ...cardStyles.legendValue, color: colors.gp1 }}>N/A</Text>
                            ) : (
                              <Text style={{ ...cardStyles.legendValue, color: colors.gp1 }}>
                                {formatNumber(data.dashboard.averageIncidentFrequency?.meantimeBetweenNearMisses)} days
                              </Text>
                            )}
                          </View>
                        </View>
                      )}
                    </>
                  )}
                </>
              ),
            }}
            slot5={{
              title: 'Risk Avoidance',
              children: (
                <>
                  <Text style={{ ...cardStyles.subLabel, marginBottom: 22 }}>
                    Security programme has avoided{' '}
                    <Text style={{ fontWeight: 600 }}>
                      {data.dashboard.riskAvoidance?.nearMisses || 0} near miss
                      {data.dashboard.riskAvoidance?.nearMisses === 1 ? '' : 'es'}
                    </Text>{' '}
                    in the last {data.dashboard.riskAvoidance?.days || 0} days, which could have resulted in losses of:
                  </Text>
                  <View style={{ flex: 1 }} />
                  {state === 'NO_SCENARIOS' ? (
                    <Danger />
                  ) : (
                    <>
                      {!hasIncidents ? (
                        <Danger />
                      ) : (
                        <View>
                          {data.dashboard.riskAvoidance?.nearMisses ? (
                            <Text style={{ ...cardStyles.value, color: colors.gp1 }}>
                              {numeralFormat(data.dashboard.riskAvoidance?.estimatedLossLower)} -{' '}
                              {numeralFormat(data.dashboard.riskAvoidance?.estimatedLossUpper)}
                            </Text>
                          ) : (
                            <Text style={{ ...cardStyles.value, color: colors.gp1 }}>N/A</Text>
                          )}
                        </View>
                      )}
                    </>
                  )}
                </>
              ),
            }}
            slot6={{
              title: 'Missing Scenario Data',
              children: (
                <View>
                  <View style={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                    <View style={cardStyles.column}>
                      <Text style={cardStyles.label}>SCENARIOS</Text>
                      <Text style={{ ...cardStyles.subLabelItalic, fontStyle: 'italic' }}>
                        without calculation data
                      </Text>
                      <View style={{ flex: 1 }} />
                      {state === 'NO_SCENARIOS' ? (
                        <Danger />
                      ) : (
                        <Text style={cardStyles.value}>
                          {data.dashboard.missingData?.scenariosWithoutCalculationData || 0}
                        </Text>
                      )}
                    </View>
                    <View style={cardStyles.column}>
                      <Text style={cardStyles.label}>SCENARIOS</Text>
                      <Text style={{ ...cardStyles.subLabelItalic, fontStyle: 'italic' }}>without linked controls</Text>
                      <View style={{ flex: 1 }} />
                      {state === 'NO_SCENARIOS' ? (
                        <Danger />
                      ) : (
                        <Text style={cardStyles.value}>
                          {data.dashboard.missingData?.scenariosWithoutLinkedControls || 0}
                        </Text>
                      )}
                    </View>
                    <View style={cardStyles.column}>
                      <Text style={cardStyles.label}>SCENARIOS</Text>
                      <Text style={{ ...cardStyles.subLabelItalic, fontStyle: 'italic' }}>without modifiers</Text>
                      <View style={{ flex: 1 }} />
                      {state === 'NO_SCENARIOS' ? (
                        <Danger />
                      ) : (
                        <Text style={cardStyles.value}>
                          {data.dashboard.missingData?.scenariosWithoutModifiers || 0}
                        </Text>
                      )}
                    </View>
                  </View>
                </View>
              ),
            }}
          />

          <AssessmentEnd
            label="INSIGHTS"
            assessmentName={data.assessment?.name!}
            colors={colors}
            orientation="landscape"
            logoSrc={brandingLogo}
            primaryColor={brandingPrimaryColor}
            secondaryColor={brandingSecondaryColor}
          />
        </Document>
      </PDFViewer>
    </HtmlWrap>
  );
};

export default PdfInsights;
